import isEmpty from "lodash-es/isEmpty";
import {
  type Control,
  type FieldPath,
  type FieldValues,
  useController,
} from "react-hook-form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/molecules/select";
import {
  FieldViewWrapper,
  type FieldViewWrapperProperties,
} from "@/components/molecules/form/FieldViewWrapper";
import { EDITOR_CONTENT_SELECT_TRIGGER_CLASS } from "@/lib/ui/editorContentChrome";

export interface SelectFieldViewProps<
  TFieldValues extends FieldValues = Record<string, unknown>,
> extends Omit<FieldViewWrapperProperties, "children"> {
  control: Control<TFieldValues>;
  name: string;
  option1: string;
  option2: string;
  defaultValue?: string;
  modal?: boolean;
  disable?: boolean;
  /** Called when value changes (e.g. for editor Redux sync). */
  onValueChange?: (value: string) => void;
}

export const SelectFieldView = <
  TFieldValues extends FieldValues = Record<string, unknown>,
>({
  label,
  id,
  control,
  defaultValue,
  style,
  name,
  option1,
  option2,
  errorMessage,
  labelClassName,
  toHide = false,
  className,
  onValueChange,
  disable = false,
}: SelectFieldViewProps<TFieldValues>) => {
  const { field } = useController({
    name: name as FieldPath<TFieldValues>,
    control,
    defaultValue: defaultValue as Parameters<
      typeof useController<TFieldValues>
    >[0]["defaultValue"],
    rules: { required: false },
  });
  const hasError = !isEmpty(errorMessage);
  const value = field.value ?? "";

  return (
    <FieldViewWrapper
      id={id}
      label={label}
      errorMessage={errorMessage}
      toHide={toHide}
      className={className ?? "relative text-left"}
      style={style}
      labelClassName={labelClassName}
      labelUseIdForA11y
    >
      <Select
        value={value}
        disabled={disable}
        onValueChange={(v) => {
          field.onChange(v);
          field.onBlur();
          onValueChange?.(v);
        }}
      >
        <SelectTrigger
          id={id}
          data-editor-select-trigger=""
          aria-labelledby={`${id}-label`}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${id}-error` : undefined}
          disabled={disable}
          className={EDITOR_CONTENT_SELECT_TRIGGER_CLASS}
        >
          <SelectValue placeholder={label} />
        </SelectTrigger>
        <SelectContent dropdownPanel>
          <SelectItem value={option1}>{option1}</SelectItem>
          <SelectItem value={option2}>{option2}</SelectItem>
        </SelectContent>
      </Select>
    </FieldViewWrapper>
  );
};
